/**
 * Command Manager Infrastructure
 * Implements the GoF Command Pattern for reliable, transactional Undo / Redo.
 * Supports memory bounding (maxStackSize), asynchronous command execution,
 * state change subscriptions, and clean serialization.
 */

export interface Command {
  id: string;
  name: string;
  timestamp: number;
  execute(): void | Promise<void>;
  undo(): void | Promise<void>;
  redo?(): void | Promise<void>;
}

export interface CommandManagerState {
  canUndo: boolean;
  canRedo: boolean;
  undoCount: number;
  redoCount: number;
  lastCommandName?: string;
  nextRedoName?: string;
  lastCommandTimestamp?: number;
  nextRedoTimestamp?: number;
}

export type CommandListener = (state: CommandManagerState) => void;

let lastIssuedActionTimestamp = 0;

/**
 * Generates a strictly monotonic timestamp so rapid sequential actions
 * (even within the same millisecond) preserve exact chronological ordering
 * between CommandManager commands and snapshot history entries.
 */
export function getNextActionTimestamp(candidate = Date.now()): number {
  const base = typeof candidate === 'number' && !Number.isNaN(candidate) ? candidate : Date.now();
  const next = Math.max(base, lastIssuedActionTimestamp + 1);
  lastIssuedActionTimestamp = next;
  return next;
}

export class CommandManager {
  private undoStack: Command[] = [];
  private redoStack: Command[] = [];
  private maxStackSize: number;
  private isProcessing = false;
  private isRedoing = false;
  private listeners = new Set<CommandListener>();

  constructor(maxStackSize = 30) {
    this.maxStackSize = Math.max(5, maxStackSize);
  }

  /**
   * Returns current state of the command manager.
   */
  public getState(): CommandManagerState {
    const lastCmd = this.undoStack[this.undoStack.length - 1];
    const nextRedoCmd = this.redoStack[this.redoStack.length - 1];

    return {
      canUndo: this.undoStack.length > 0,
      canRedo: this.redoStack.length > 0,
      undoCount: this.undoStack.length,
      redoCount: this.redoStack.length,
      lastCommandName: lastCmd?.name,
      nextRedoName: nextRedoCmd?.name,
      lastCommandTimestamp: lastCmd?.timestamp,
      nextRedoTimestamp: nextRedoCmd?.timestamp,
    };
  }

  /**
   * Subscribes a listener to stack changes.
   * Returns an unsubscribe function.
   */
  public subscribe(listener: CommandListener): () => void {
    this.listeners.add(listener);
    listener(this.getState());
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notify(): void {
    const state = this.getState();
    this.listeners.forEach(fn => {
      try {
        fn(state);
      } catch (err) {
        console.error('[CommandManager] Listener error:', err);
      }
    });
  }

  /**
   * Executes a command and pushes it onto the undo stack.
   * Clears the redo stack.
   */
  public async execute(command: Command): Promise<void> {
    if (this.isProcessing) {
      console.warn('[CommandManager] Cannot execute while another command is being processed.');
      return;
    }

    this.isProcessing = true;
    try {
      await command.execute();
      this.registerExecuted(command);
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Registers a command whose execution was already completed optimistically.
   * Pushes onto undo stack, prunes older commands if beyond maxStackSize,
   * and clears the redo stack.
   */
  public registerExecuted(command: Command): void {
    if (this.isRedoing) {
      // When command.redo() re-invokes an action that calls registerExecuted,
      // do not double-push onto undoStack or wipe remaining redoStack entries.
      return;
    }
    command.timestamp = getNextActionTimestamp(command.timestamp);
    this.undoStack.push(command);
    if (this.undoStack.length > this.maxStackSize) {
      this.undoStack.shift();
    }
    this.redoStack = [];
    this.notify();
  }

  /**
   * Clears only the redo stack (e.g. when a new snapshot action occurs).
   */
  public clearRedo(): void {
    if (this.redoStack.length === 0) return;
    this.redoStack = [];
    this.notify();
  }

  public get lastUndoTimestamp(): number | undefined {
    return this.undoStack[this.undoStack.length - 1]?.timestamp;
  }

  public get nextRedoTimestamp(): number | undefined {
    return this.redoStack[this.redoStack.length - 1]?.timestamp;
  }

  public get isCurrentlyRedoing(): boolean {
    return this.isRedoing;
  }

  /**
   * Undoes the most recent command.
   * Returns the command that was undone, or null if no command was available.
   */
  public async undo(): Promise<Command | null> {
    if (this.isProcessing || this.undoStack.length === 0) {
      return null;
    }

    this.isProcessing = true;
    const command = this.undoStack.pop()!;
    try {
      await command.undo();
      this.redoStack.push(command);
      if (this.redoStack.length > this.maxStackSize) {
        this.redoStack.shift();
      }
      this.notify();
      return command;
    } catch (err) {
      console.error(`[CommandManager] Undo failed for command "${command.name}":`, err);
      // Re-insert command back into undo stack on failure
      this.undoStack.push(command);
      this.notify();
      throw err;
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Redoes the next command in the redo stack.
   * Returns the command that was redone, or null if no command was available.
   */
  public async redo(): Promise<Command | null> {
    if (this.isProcessing || this.redoStack.length === 0) {
      return null;
    }

    this.isProcessing = true;
    this.isRedoing = true;
    const command = this.redoStack.pop()!;
    try {
      if (typeof command.redo === 'function') {
        await command.redo();
      } else {
        await command.execute();
      }
      this.undoStack.push(command);
      if (this.undoStack.length > this.maxStackSize) {
        this.undoStack.shift();
      }
      this.notify();
      return command;
    } catch (err) {
      console.error(`[CommandManager] Redo failed for command "${command.name}":`, err);
      this.redoStack.push(command);
      this.notify();
      throw err;
    } finally {
      this.isRedoing = false;
      this.isProcessing = false;
    }
  }

  /**
   * Clears both undo and redo stacks.
   */
  public clear(): void {
    this.undoStack = [];
    this.redoStack = [];
    this.notify();
  }

  /**
   * Checks if an undo operation is available.
   */
  public get canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  /**
   * Checks if a redo operation is available.
   */
  public get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  /**
   * Returns a copy of the current undo history (newest last).
   */
  public getHistory(): { id: string; name: string; timestamp: number }[] {
    return this.undoStack.map(c => ({
      id: c.id,
      name: c.name,
      timestamp: c.timestamp
    }));
  }
}

/**
 * Singleton instance of CommandManager for system-wide coordination.
 */
export const globalCommandManager = new CommandManager(30);

/**
 * Checks whether an event target is an interactive text input element.
 * Used by keyboard shortcut handlers to prevent intercepting native input undo.
 */
export function isEditableElement(target: EventTarget | null): boolean {
  if (!target || !(target instanceof HTMLElement)) return false;
  const tag = target.tagName.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if (target.isContentEditable) return true;
  return false;
}

